import { IconChevronDown, IconTransfer } from '@tabler/icons-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ChartType } from '@/components/ui/charts/types/chart-props'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { selectRegions, selectRegionsLoading, useAppSelector } from '@/store'
import { Region } from '@/types'
import { cn, getMetricFilters } from '@/utils'
import { ComparisonSelection } from '../ui/charts/types'

interface ScalrSelectProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any[]
  onValueChange?: (newValue: ComparisonSelection) => void
  placeholder?: string
  selectedBranch?: string
  onBranchChange?: (branch: string) => void
  chartType: ChartType
}

interface SecondColumnProps {
  selectedSecondColumn: string | null
  setSelectedSecondColumn: (value: string) => void
  selectableRegions: Region[]
  selectedThirdColumn: string | null
}

interface ThirdColumnProps {
  selectedThirdColumn: string | null
  setSelectedThirdColumn: (value: string) => void
  selectableRegions: Region[]
  selectedSecondColumn: string | null
}

const firstColumnOptions = [
  { value: 'Branches', label: 'Branches' },
  // { value: 'Metric', label: 'Metrics' },
  { value: 'Period', label: 'Period' },
]

const PERIODS = ['2025', '2024', '2023', '2022', '2021', '2020', '2019', '2018']

export function ScalrSelect({
  onValueChange,
  placeholder = 'Select Comparison',
  selectedBranch,
  onBranchChange,
  chartType,
}: ScalrSelectProps) {
  const { id } = useParams()
  const REGIONS = useAppSelector(selectRegions)
  const regionsLoading = useAppSelector(selectRegionsLoading)
  const [selectableRegions, setSelectableRegions] = useState<Region[]>([])

  const [open, setOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState<string | null>(null)

  const [selectedFirstColumn, setSelectedFirstColumn] = useState<string | null>(
    'Branches'
  )
  const [selectedSecondColumn, setSelectedSecondColumn] = useState<
    string | null
  >(selectedBranch?.includes(',') ? selectedBranch : null)
  const [selectedThirdColumn, setSelectedThirdColumn] = useState<string | null>(
    null
  )

  // Update selectable regions based on current page and regions data
  useEffect(() => {
    if (REGIONS.length === 0) return

    if (id !== 'spend') {
      setSelectableRegions(REGIONS.filter((region) => region.name !== 'Other'))
    } else {
      setSelectableRegions(REGIONS)
    }
  }, [id, REGIONS])

  // Sync internal state with prop when it changes
  useEffect(() => {
    if (selectedBranch) {
      const branchName = selectedBranch.split(',')[0]
      if (branchName !== selectedSecondColumn) {
        setSelectedSecondColumn(branchName)
      }

      if (
        selectedFirstColumn === 'Branches' &&
        selectedThirdColumn &&
        branchName !== selectedSecondColumn
      ) {
        const newSecondColumn = branchName
        const currentThirdColumn = selectedThirdColumn

        setSelectedValue(`${newSecondColumn} - ${currentThirdColumn}`)
        onValueChange?.({
          type: 'branches',
          value: `${newSecondColumn},${currentThirdColumn}`,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]) // selectedFirstColumn, selectedThirdColumn are dependencies too but we want this primarily on selectedBranch change

  const handleFirstColumnClick = useCallback(
    (value: string) => {
      setSelectedFirstColumn(value)
      if (value !== selectedFirstColumn) {
        setSelectedSecondColumn(null)
        setSelectedThirdColumn(null)
      }
    },
    [selectedFirstColumn]
  )

  const applyFilter = useCallback(() => {
    if (selectedSecondColumn && selectedThirdColumn) {
      // If we are comparing Branches, update the main branch selection too
      if (
        selectedFirstColumn === 'Branches' &&
        selectedBranch !== selectedSecondColumn
      ) {
        onBranchChange?.(selectedSecondColumn)
      }

      setSelectedValue(`${selectedSecondColumn} - ${selectedThirdColumn}`)
      onValueChange?.({
        type: selectedFirstColumn?.toLowerCase() as ComparisonSelection['type'],
        value: selectedThirdColumn
          ? `${selectedSecondColumn},${selectedThirdColumn}`
          : selectedSecondColumn,
      })
      if (
        (selectedFirstColumn?.toLowerCase() as ComparisonSelection['type']) ===
        'period'
      ) {
        localStorage.setItem('scalerSelectType', 'period')
      }
      setOpen(false)
    }
  }, [
    selectedSecondColumn,
    selectedThirdColumn,
    selectedFirstColumn,
    onValueChange,
    onBranchChange,
    selectedBranch,
  ])

  // Show loading state when regions are loading
  if (regionsLoading || REGIONS.length === 0) {
    return (
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={false}
        className="h-8 justify-between"
        disabled
      >
        Loading...
      </Button>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 justify-between font-normal"
        >
          {selectedValue ? (
            selectedValue
          ) : (
            <div className="flex items-center gap-2">
              <IconTransfer className="h-4 w-4" />
              {placeholder}
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-4 w-[60vh]">
        <div className="flex flex-col h-[420px] space-y-4">
          {/* Top 3 buttons */}
          <div className="flex gap-2">
            {firstColumnOptions.map((option) => (
              <Button
                key={option.value}
                variant="outline"
                onClick={() => handleFirstColumnClick(option.value)}
                className={cn(
                  'flex-1 font-normal',
                  selectedFirstColumn === option.value
                    ? 'border-blue-500 text-blue-500 hover:border-blue-500 hover:text-blue-500'
                    : 'hover:border-blue-500 hover:text-blue-500'
                )}
              >
                {option.label}
              </Button>
            ))}
          </div>

          {/* Separator */}
          <Separator />

          {/* Selection and Comparison columns */}
          <div className="grid grid-cols-[1fr_auto_1fr] flex-1">
            {/* Selection Card */}
            <div className="flex flex-col items-center">
              <h2 className="text-sm font-medium text-muted-foreground mb-2">
                Selection
              </h2>
              <Card>
                <CardContent className="p-0">
                  <div className="h-[260px] overflow-y-auto">
                    {selectedFirstColumn === 'Branches' && (
                      <RegionSelectSecondColumn
                        selectedSecondColumn={selectedSecondColumn}
                        setSelectedSecondColumn={setSelectedSecondColumn}
                        selectableRegions={selectableRegions}
                        selectedThirdColumn={selectedThirdColumn}
                      />
                    )}
                    {selectedFirstColumn === 'Metric' &&
                      getMetricFilters(chartType).map((metric) => (
                        <Button
                          key={metric.value}
                          variant="ghost"
                          onClick={() => setSelectedSecondColumn(metric.value)}
                          disabled={metric.value === selectedThirdColumn}
                          className={cn(
                            'w-full justify-start font-normal mb-1',
                            selectedSecondColumn === metric.value && 'bg-accent'
                          )}
                        >
                          {metric.label}
                        </Button>
                      ))}
                    {selectedFirstColumn === 'Period' &&
                      PERIODS.map((period) => (
                        <Button
                          key={period}
                          variant="ghost"
                          onClick={() => setSelectedSecondColumn(period)}
                          disabled={period === selectedThirdColumn}
                          className={cn(
                            'w-full justify-start font-normal mb-1',
                            selectedSecondColumn === period && 'bg-accent'
                          )}
                        >
                          {period}
                        </Button>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Icon between cards */}
            <div className="flex items-start justify-center pt-1">
              <IconTransfer className="h-4 w-4 text-muted-foreground" />
            </div>

            {/* Comparison Card */}
            <div className="flex flex-col items-center">
              <h2 className="text-sm font-medium text-muted-foreground mb-2">
                Comparison
              </h2>
              <Card>
                <CardContent className="p-0">
                  <div className="h-[260px] overflow-y-auto">
                    {selectedFirstColumn === 'Branches' && (
                      <RegionSelectThirdColumn
                        selectedThirdColumn={selectedThirdColumn}
                        setSelectedThirdColumn={setSelectedThirdColumn}
                        selectableRegions={selectableRegions}
                        selectedSecondColumn={selectedSecondColumn}
                      />
                    )}
                    {selectedFirstColumn === 'Metric' &&
                      getMetricFilters(chartType).map((metric) => (
                        <Button
                          key={metric.value}
                          variant="ghost"
                          onClick={() => setSelectedThirdColumn(metric.value)}
                          disabled={metric.value === selectedSecondColumn}
                          className={cn(
                            'w-full justify-start font-normal mb-1',
                            selectedThirdColumn === metric.value && 'bg-accent'
                          )}
                        >
                          {metric.label}
                        </Button>
                      ))}
                    {selectedFirstColumn === 'Period' &&
                      PERIODS.map((period) => (
                        <Button
                          key={period}
                          variant="ghost"
                          onClick={() => setSelectedThirdColumn(period)}
                          disabled={period === selectedSecondColumn}
                          className={cn(
                            'w-full justify-start font-normal mb-1',
                            selectedThirdColumn === period && 'bg-accent'
                          )}
                        >
                          {period}
                        </Button>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Apply Filter Button at bottom */}
          <Button
            className="w-full"
            onClick={applyFilter}
            disabled={!selectedSecondColumn || !selectedThirdColumn}
          >
            Apply Filter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function RegionSelectSecondColumn({
  selectedSecondColumn,
  setSelectedSecondColumn,
  selectableRegions,
  selectedThirdColumn,
}: SecondColumnProps) {
  return (
    <div>
      {selectableRegions.map((region) => (
        <Fragment key={`region-${region.id}`}>
          <Button
            variant="ghost"
            role="option"
            className={cn(
              'w-full justify-start font-normal hover:bg-transparent mb-1',
              selectedSecondColumn === region.name && 'bg-accent'
            )}
          >
            {region.name}
            <IconChevronDown className="ml-auto h-4 w-4" />
          </Button>
          {region.branches.map((branch) => (
            <Button
              key={`branch-${branch.id}`}
              variant="ghost"
              role="option"
              onClick={() => setSelectedSecondColumn(branch.name)}
              disabled={branch.name === selectedThirdColumn}
              className={cn(
                'w-full justify-start font-normal pl-6 mb-1',
                selectedSecondColumn === branch.name && 'bg-accent'
              )}
            >
              {branch.name}
            </Button>
          ))}
        </Fragment>
      ))}
    </div>
  )
}

function RegionSelectThirdColumn({
  selectedThirdColumn,
  setSelectedThirdColumn,
  selectableRegions,
  selectedSecondColumn,
}: ThirdColumnProps) {
  return (
    <div>
      {selectableRegions.map((region) => (
        <Fragment key={`region-${region.id}`}>
          <Button
            variant="ghost"
            role="option"
            className={cn(
              'w-full justify-start font-normal hover:bg-transparent mb-1',
              selectedThirdColumn === region.name && 'bg-accent'
            )}
          >
            {region.name}
            <IconChevronDown className="ml-auto h-4 w-4" />
          </Button>
          {region.branches.map((branch) => (
            <Button
              key={`branch-${branch.id}`}
              variant="ghost"
              role="option"
              onClick={() => setSelectedThirdColumn(branch.name)}
              disabled={branch.name === selectedSecondColumn}
              className={cn(
                'w-full justify-start font-normal pl-6 mb-1',
                selectedThirdColumn === branch.name && 'bg-accent'
              )}
            >
              {branch.name}
            </Button>
          ))}
        </Fragment>
      ))}
    </div>
  )
}
